/**
 * Player ranking projections (overall / season / state / org / age / discipline).
 */

import { calculateCareerStatistics } from "../player/playerCareer";
import {
  CURRENT_SEASON_KEY,
  DEFAULT_POINTS_TABLE,
  DISCIPLINES,
  inferAgeCategory,
  inferDisciplineFromCard,
  resolveSeasonYear,
  seasonKeyForYear,
  seasonKeyFromTimestamp,
} from "./rankingTypes";
import { calculatePointsFromSources, bestPlacementsPerCompetition } from "./pointsEngine";
import { awardAchievements, projectRecentForm } from "./achievements";

/**
 * Filter cards by season year.
 * @param {Array<object>} cards
 * @param {number | null} seasonYear
 */
export function filterCardsBySeason(cards, seasonYear) {
  if (seasonYear == null) return cards || [];
  return (cards || []).filter((c) => {
    const y = Number(seasonKeyFromTimestamp(c.completedAt));
    return y === seasonYear;
  });
}

/**
 * @param {object} input
 * @param {string} input.playerId
 * @param {Array<object>} [input.cards]
 * @param {Array<{ playerId: string, placement: string, competitionId?: string }>} [input.placements]
 * @param {object} [input.profile] club/state/country/dateOfBirth/organizationIds
 * @param {string} [input.seasonKey]
 * @param {typeof DEFAULT_POINTS_TABLE} [input.pointsTable]
 * @param {number} [input.previousBestRank]
 */
export function calculatePlayerRankings(input = {}) {
  const playerId = input.playerId;
  const seasonKey = input.seasonKey || CURRENT_SEASON_KEY;
  const seasonYear = resolveSeasonYear(seasonKey);
  const table = input.pointsTable || DEFAULT_POINTS_TABLE;
  const profile = input.profile || {};
  const allCards = input.cards || [];
  const seasonCards = filterCardsBySeason(allCards, seasonYear);
  const myPlacements = bestPlacementsPerCompetition(
    (input.placements || []).filter((p) => p.playerId === playerId)
  );

  const points = calculatePointsFromSources({
    placements: myPlacements,
    cards: seasonCards,
    pointsTable: table,
    seasonYear,
  });

  const careerStats = calculateCareerStatistics(allCards);
  const seasonStats = calculateCareerStatistics(seasonCards);
  const achievements = awardAchievements({
    cards: allCards,
    placements: myPlacements,
    seasonYear,
  });
  const recentForm = projectRecentForm(allCards, 10);

  /** @type {Record<string, number>} */
  const byDiscipline = {};
  const disciplineBuckets = {};
  seasonCards.forEach((c) => {
    const d = inferDisciplineFromCard(c);
    const key = d === DISCIPLINES.UNKNOWN ? DISCIPLINES.OPEN : d;
    if (!disciplineBuckets[key]) disciplineBuckets[key] = [];
    disciplineBuckets[key].push(c);
  });
  Object.entries(disciplineBuckets).forEach(([code, cards]) => {
    const pts = calculatePointsFromSources({
      placements: myPlacements,
      cards,
      pointsTable: table,
      seasonYear,
    });
    byDiscipline[code] = pts.points;
  });

  const ageCategory = inferAgeCategory(profile.dateOfBirth);
  const highestPoints = Math.max(
    points.points,
    typeof profile.highestPoints === "number" ? profile.highestPoints : 0
  );

  return {
    playerId,
    seasonKey,
    seasonYear: seasonYear ?? new Date().getFullYear(),
    algorithmId: table.algorithmId,
    overall: {
      points: points.points,
      breakdown: points.breakdown,
      rank: null, // filled by leaderboard assembly
      previousRank: input.previousRank ?? null,
    },
    season: {
      key: seasonKey,
      year: seasonYear,
      points: points.points,
      matches: seasonStats.matchesPlayed,
      wins: seasonStats.wins,
      winPct: seasonStats.careerWinPct,
    },
    state: {
      state: profile.state || null,
      points: points.points,
    },
    organization: {
      organizationIds: profile.organizationIds || [],
      points: points.points,
    },
    age: {
      category: ageCategory,
      points: points.points,
    },
    discipline: byDiscipline,
    profileStats: {
      currentRanking: null,
      highestRanking: input.previousBestRank ?? null,
      seasonPoints: points.points,
      careerTitles: careerStats.titles + myPlacements.filter((p) => p.placement === "winner").length,
      finals:
        careerStats.titles +
        careerStats.runnerUps +
        myPlacements.filter((p) => p.placement === "winner" || p.placement === "runner_up").length,
      winPct: careerStats.careerWinPct,
      recentForm,
      highestPoints,
    },
    achievements,
    careerStats,
    seasonStats,
    displayName: profile.displayName || null,
    country: profile.country || null,
    stateName: profile.state || null,
    club: profile.club || null,
  };
}

/**
 * Assign ranks 1..n by points among player ranking rows.
 * @param {Array<object>} rows result of calculatePlayerRankings
 */
export function assignRanks(rows) {
  const sorted = [...(rows || [])].sort(
    (a, b) => (b.overall?.points || 0) - (a.overall?.points || 0)
  );
  sorted.forEach((row, i) => {
    row.overall.rank = i + 1;
    row.profileStats.currentRanking = i + 1;
    if (
      row.profileStats.highestRanking == null ||
      i + 1 < row.profileStats.highestRanking
    ) {
      row.profileStats.highestRanking = i + 1;
    }
  });
  return sorted;
}

/**
 * Calculate season standings board from player ranking rows / snapshots.
 * @param {Array<object>} playerRows
 * @param {{ seasonKey?: string, discipline?: string, state?: string, country?: string, organizationId?: string, ageCategory?: string }} filters
 */
export function calculateSeasonStandings(playerRows, filters = {}) {
  const seasonKey = filters.seasonKey || CURRENT_SEASON_KEY;
  let rows = (playerRows || []).filter((r) => !r.seasonKey || r.seasonKey === seasonKey);

  if (filters.state) {
    const s = filters.state.toLowerCase();
    rows = rows.filter((r) => (r.stateName || r.state?.state || "").toLowerCase().includes(s));
  }
  if (filters.country) {
    const c = filters.country.toLowerCase();
    rows = rows.filter((r) => (r.country || "").toLowerCase().includes(c));
  }
  if (filters.organizationId) {
    rows = rows.filter((r) =>
      (r.organization?.organizationIds || []).includes(filters.organizationId)
    );
  }
  if (filters.ageCategory) {
    rows = rows.filter((r) => r.age?.category === filters.ageCategory);
  }
  if (filters.discipline && filters.discipline !== DISCIPLINES.UNKNOWN) {
    rows = rows
      .map((r) => {
        const pts = r.discipline?.[filters.discipline];
        if (pts == null) return null;
        return {
          ...r,
          overall: { ...r.overall, points: pts },
        };
      })
      .filter(Boolean);
  }

  return assignRanks(rows);
}

export function buildSeasonTransitionNote(fromYear, toYear) {
  return {
    fromSeason: seasonKeyForYear(fromYear),
    toSeason: seasonKeyForYear(toYear),
    message: `Season ${fromYear} closed. Rankings for ${toYear} start fresh (points_mvp).`,
  };
}
